/**
 * The browser's half of the assistant protocol: four fetches and a decoder.
 *
 * No client library, for the same reason `auth/client.ts` has none — the surface
 * is small enough to read in one file, and a hand-rolled reader is trivially
 * fake-able in a test. The only subtlety is that `POST /api/ai/chat` answers
 * with a stream, so it is consumed with `response.body` rather than `.json()`.
 */

import type { AiAllowance } from '@/lib/plan';
import { parseAllowance } from '@/lib/plan';
import type { AiChatRequest, AiStreamEvent, AiThreadEntry, AiThreadSummary } from './protocol';
import { AI_BASE_PATH, SseDecoder } from './protocol';

/** A failure that happened before the stream opened, with the server's wording. */
export interface AiRequestError {
  readonly status: number;
  readonly code: string;
  readonly message: string;
  /**
   * The month's allowance, when the failure was `rate_limited`.
   *
   * The 429 body carries the plan, the usage, the limit and the reset month, so
   * the drawer can render the exhausted state — with a real date and a real
   * number — from the rejection alone, without a second round trip.
   */
  readonly allowance?: AiAllowance;
}

/** This month's assistant allowance, or `null` when it could not be read. */
export async function fetchAllowance(): Promise<AiAllowance | null> {
  try {
    const response = await fetch(`${AI_BASE_PATH}/usage`, {
      headers: { accept: 'application/json' },
    });
    if (!response.ok) return null;
    return parseAllowance(await response.json());
  } catch {
    return null;
  }
}

/**
 * Thread ids are derived, not minted: one per recipe, one per blend, one per
 * batch, plus one global.
 *
 * Derived means a page never has to *find* its conversation — it says what it is
 * showing and the id falls out, so the same beer's thread is waiting the next
 * time it is opened, on any device. Most specific first: a page shows one of
 * these at a time, and where two are somehow set the narrower subject wins.
 */
export function threadIdFor(
  recipeId: string | null,
  blendId: string | null = null,
  batchId: string | null = null,
): string {
  if (blendId !== null) return `thread-blend-${blendId}`;
  if (batchId !== null) return `thread-batch-${batchId}`;
  return recipeId === null ? 'thread-global' : `thread-recipe-${recipeId}`;
}

/** What a thread is about, read back out of its id. */
export type AiThreadSubject =
  | { readonly kind: 'recipe'; readonly recipeId: string }
  | { readonly kind: 'blend'; readonly blendId: string }
  | { readonly kind: 'batch'; readonly batchId: string }
  | { readonly kind: 'global' };

/**
 * The inverse of {@link threadIdFor}, and the *only* place an id is read.
 *
 * Deriving ids means the id already carries the subject, so the thread list can
 * say "this one is a batch" and open the batch page without a column for it.
 * That is worth exactly one rule: ids are built here and taken apart here, and
 * nowhere else — the server still treats them as opaque keys, and a caller that
 * wants the subject asks this function rather than matching a prefix of its own.
 *
 * Anything that does not match falls back to the workspace thread, which is what
 * an unrecognised id has always meant to the drawer.
 */
export function subjectOfThreadId(threadId: string): AiThreadSubject {
  if (threadId.startsWith('thread-recipe-')) {
    return { kind: 'recipe', recipeId: threadId.slice('thread-recipe-'.length) };
  }
  if (threadId.startsWith('thread-blend-')) {
    return { kind: 'blend', blendId: threadId.slice('thread-blend-'.length) };
  }
  if (threadId.startsWith('thread-batch-')) {
    return { kind: 'batch', batchId: threadId.slice('thread-batch-'.length) };
  }
  return { kind: 'global' };
}

/** Every thread, or only those attached to one recipe. */
export async function fetchThreads(recipeId?: string | null): Promise<readonly AiThreadSummary[]> {
  const query = recipeId === undefined ? '' : `?recipeId=${encodeURIComponent(recipeId ?? '')}`;
  try {
    const response = await fetch(`${AI_BASE_PATH}/threads${query}`, {
      headers: { accept: 'application/json' },
    });
    if (!response.ok) return [];
    const body = (await response.json()) as { threads?: readonly AiThreadSummary[] };
    return body.threads ?? [];
  } catch {
    return [];
  }
}

/** One thread's transcript. An empty array covers "never used" and "offline". */
export async function fetchThread(threadId: string): Promise<readonly AiThreadEntry[]> {
  try {
    const response = await fetch(`${AI_BASE_PATH}/threads/${encodeURIComponent(threadId)}`, {
      headers: { accept: 'application/json' },
    });
    if (!response.ok) return [];
    const body = (await response.json()) as { entries?: readonly AiThreadEntry[] };
    return body.entries ?? [];
  } catch {
    return [];
  }
}

/**
 * Throw one thread away, messages and all.
 *
 * Answers `true` for "it is gone", which includes the thread that was never
 * there — the caller asked for a state, not for a row to be found. A network
 * failure is `false`, and the drawer keeps what it was showing rather than
 * pretending a delete landed.
 */
export async function deleteThread(threadId: string): Promise<boolean> {
  try {
    const response = await fetch(`${AI_BASE_PATH}/threads/${encodeURIComponent(threadId)}`, {
      method: 'DELETE',
      headers: { accept: 'application/json' },
    });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Send a message and stream the answer.
 *
 * `onEvent` is called for every frame in arrival order. A failure that happens
 * *before* the stream opens is thrown as an {@link AiRequestError} — those are
 * the states the drawer renders differently (a setup notice, a quota notice)
 * rather than as a message in the conversation. Once the stream is open, every
 * failure arrives as an `error` frame and is just another event.
 */
export async function streamChat(
  request: AiChatRequest,
  onEvent: (event: AiStreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(`${AI_BASE_PATH}/chat`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(request),
    ...(signal === undefined ? {} : { signal }),
  });

  if (!response.ok || response.body === null) {
    throw await readRequestError(response);
  }

  const reader = response.body.getReader();
  const decoder = new SseDecoder();
  const text = new TextDecoder();
  for (;;) {
    const chunk = await reader.read();
    if (chunk.done) break;
    for (const event of decoder.push(text.decode(chunk.value, { stream: true }))) {
      onEvent(event);
    }
  }
}

async function readRequestError(response: Response): Promise<AiRequestError> {
  try {
    const body = (await response.json()) as { error?: string; message?: string };
    const allowance = parseAllowance(body);
    return {
      status: response.status,
      code: body.error ?? 'internal',
      message: body.message ?? 'We could not reach the assistant.',
      ...(allowance === null ? {} : { allowance }),
    };
  } catch {
    return {
      status: response.status,
      code: 'internal',
      message: 'We could not reach the assistant.',
    };
  }
}
